/**
 * AddFab — Mobile Floating Action Button for task/section creation.
 *
 * Tap → create task at standard position via addTask().
 * Long-press + drag → drag a "ghost" task row into the list.
 *               → drag to left edge (~60px) to switch to section mode.
 *               → drag to cancel zone (bottom-right, FAB origin) to abort.
 *               → drag to quick-add zone (bottom-left) to open QuickAddPanel.
 *               → release in list to create at the indicated position.
 * "..." badge → speed dial sub-menu with Quick Add, Section, etc.
 *
 * Visibility: shown ONLY when ActionPanel is hidden on mobile
 * (no-selection, not dragging, not logbook/trash/search).
 *
 * Uses the operations layer exclusively (addTask / addSection).
 * Section creation constrained to container views (project/list).
 *
 * The drag overlay renders the FAB icon itself — following the finger during
 * drag. It grows slightly with a shadow when snapped to a magnetic zone.
 * The gap indicator uses a real TaskItem with isDragOverlay via a dummy DbObject.
 */

import { memo, useCallback, useRef, useState, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import Plus from "@/shared/icons/pikaicons/stroke/maths/plus-default.svg?react"
import XIcon from "@/shared/icons/pikaicons/stroke/maths/multiple-cross-cancel-default.svg?react"
import Inbox from "@/shared/icons/pikaicons/stroke/communication/inbox-default.svg?react"
import Heading from "@/shared/icons/totodoicons/stroke/section.svg?react"
import MessageCircle from "@/shared/icons/pikaicons/stroke/communication/chat-default.svg?react"
import Paperclip from "@/shared/icons/pikaicons/stroke/files-folders/paperclip-slant.svg?react"
import { cn } from "@/shared/utils/general/cn"
import { addTask, addSection } from "@/db/operations"
import { getTaskDefaults } from "@/shared/utils/task/getTaskDefaults"
import { useSetting } from "@/features/settings/hooks/useSetting"
import { hapticLight, hapticMedium } from "@/shared/utils/platform/haptics"
import { useActiveTask } from "@/features/task-detail/hooks/useActiveTask"
import { TaskItem } from "@/features/tasks/components/TaskItem"
import { ObjectType, ObjectStatus } from "@/db/schema"
import type { DbObject } from "@/db/schema"
import { useFabState } from "./useFabState"
import { useMagneticSnap, type MagneticZone } from "./useMagneticSnap"
import { QuickAddModal } from "./QuickAddModal"
import { Capacitor } from "@capacitor/core"
import { useKeyboardStore } from "@/features/auth/stores/keyboardStore"
import { useViewContext } from "@/app/stores/viewContext"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FAB_SIZE = 56
const FAB_RIGHT = 16
const FAB_BOTTOM_OFFSET = 16
/** Long-press delay to activate drag (ms) — matches ListDragSensor */
const LONG_PRESS_MS = 250
/** Movement tolerance during long-press — abort if exceeded (px) */
const MOVE_TOLERANCE = 5
/** Left-edge threshold for section mode (px from left screen edge) */
const SECTION_ZONE_X = 60
/** How many px to shift rows below the insert point to open a gap */
const ROW_SHIFT = 50
/** Auto-scroll: dead zone from edge of scroll container (px) */
const AUTO_SCROLL_ZONE = 100
/** Auto-scroll: max speed in px/frame */
const AUTO_SCROLL_MAX_SPEED = 12
/** Auto-scroll: finger must move this far from drag start before scroll activates (px) */
const AUTO_SCROLL_ACTIVATE_DISTANCE = 20
/** Quick-add zone: size of the FAB that appears bottom-left */
const ZONE_FAB_SIZE = 52
/** Quick-add zone: left offset from screen edge */
const ZONE_LEFT = 16
/** Magnetic snap: enter radius — how close finger must get to activate gravity */
const SNAP_ENTER_RADIUS = 80
/** Magnetic snap: exit radius — must drag this far to escape (hysteresis) */
const SNAP_EXIT_RADIUS = 120
/** Magnetic snap: pull strength — viscous blend toward center (0.55 = strong resistance) */
const SNAP_PULL = 0.55
/** Speed dial: mini badge size */
const BADGE_SIZE = 28
/** Speed dial: stagger delay per item */
const SPEED_DIAL_STAGGER_MS = 50

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FabDragMode = "task" | "section"
type DropTarget = "list" | "cancel" | "quick-add"

interface DragState {
  active: boolean
  x: number
  y: number
  mode: FabDragMode
  afterId: string | null
  /** Which zone the drag is currently snapped to (null = in the list) */
  dropTarget: DropTarget
  /** True for a short duration after exiting a magnetic zone — triggers bounce animation */
  bouncing: boolean
}

// ---------------------------------------------------------------------------
// Dummy DbObject factories — used for TaskItem isDragOverlay rendering
// ---------------------------------------------------------------------------

const DUMMY_TASK: DbObject = {
  id: "__fab-dummy-task__",
  type: ObjectType.Task,
  title: "",
  parentPath: "",
  index: 0,
  status: ObjectStatus.Unfinished,
  tags: [],
  isDeleted: 0,
  createdAt: "",
  createdBy: "",
  fullTextIndex: [],
}

const DUMMY_SECTION: DbObject = {
  id: "__fab-dummy-section__",
  type: ObjectType.Section,
  title: "",
  parentPath: "",
  index: 0,
  status: ObjectStatus.Unfinished,
  tags: [],
  isDeleted: 0,
  createdAt: "",
  createdBy: "",
  fullTextIndex: [],
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AddFab = memo(function AddFab() {
  const { t } = useTranslation()
  const { visible, allowSection, viewId, containerId } = useFabState()
  const { value: eveningHour } = useSetting<number>("#evening-time", 18)
  const { openTask } = useActiveTask()

  const pressTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)
  const [dragState, setDragState] = useState<DragState | null>(null)
  const dragStateRef = useRef<DragState | null>(null)
  const autoScrollRef = useRef<number>(0)
  const scrollElRef = useRef<HTMLElement | null>(null)
  const dragStartYRef = useRef<number>(0)
  const [speedDialOpen, setSpeedDialOpen] = useState(false)
  const [quickAddOpen, setQuickAddOpen] = useState(false)
  const justClosedDialRef = useRef(false)
  // A touch that created a task is followed by the browser's synthetic click; without this the
  // click created a second, empty task (pairs of tasks a few ms apart, one left as "New To-Do").
  const touchHandledRef = useRef(false)
  // One creation at a time: a double tap must not create two tasks either.
  const creatingRef = useRef(false)

  // Magnetic snap for cancel zone (bottom-right) and quick-add zone (bottom-left)
  const cancelSnap = useMagneticSnap()
  const quickAddSnap = useMagneticSnap()

  // Position of the gap indicator (resolved to screen coordinates)
  const [gapPosition, setGapPosition] = useState<{
    top: number
    left: number
    width: number
  } | null>(null)

  useEffect(() => {
    dragStateRef.current = dragState
  }, [dragState])

  // ── Set CSS variable so toasts render above the FAB ─────────────────────
  useEffect(() => {
    if (visible) {
      const offset = FAB_SIZE + FAB_BOTTOM_OFFSET + 8
      document.documentElement.style.setProperty(
        "--fab-toast-offset",
        `calc(var(--safe-area-bottom, 0px) + ${offset}px)`,
      )
    } else {
      document.documentElement.style.removeProperty("--fab-toast-offset")
    }
    return () => {
      document.documentElement.style.removeProperty("--fab-toast-offset")
    }
  }, [visible])

  // -----------------------------------------------------------------------
  // Tap → create task via operations layer (or routine on routines view)
  // -----------------------------------------------------------------------
  const handleTap = useCallback(async () => {
    hapticLight()
    // On routines view, dispatch event to open CreateRoutineSheet
    if (viewId === "routines") {
      window.dispatchEvent(new CustomEvent("create-routine"))
      return
    }
    if (creatingRef.current) return
    creatingRef.current = true
    try {
      const defaults = getTaskDefaults(
        viewId,
        containerId ?? undefined,
        eveningHour,
      )
      const result = await addTask({
        title: "",
        parentPath: defaults.parentPath,
        insertPosition: "top",
        startAt: defaults.schedule?.startAt,
        startAtType: defaults.schedule?.type,
        startAtSubType: defaults.schedule?.subType,
      })
      if (result.ok) {
        openTask(result.data.id, true)
      }
    } finally {
      creatingRef.current = false
    }
  }, [viewId, containerId, eveningHour, openTask])

  // -----------------------------------------------------------------------
  // Find insert position from pointer Y → walk task rows
  // -----------------------------------------------------------------------
  const findInsertPosition = useCallback(
    (clientY: number): { afterId: string | null } => {
      const rows = document.querySelectorAll("[data-task-id]")
      let afterId: string | null = null
      for (const row of rows) {
        const rect = row.getBoundingClientRect()
        const midY = rect.top + rect.height / 2
        if (clientY > midY) {
          afterId = row.getAttribute("data-task-id")
        } else {
          break
        }
      }
      return { afterId }
    },
    [],
  )

  // -----------------------------------------------------------------------
  // Push rows apart — applies translateY to rows below the insertion point
  // so items visually open a gap. Called on each touchmove to survive
  // virtualizer re-renders (which reset inline styles).
  // -----------------------------------------------------------------------
  const applyRowShifts = useCallback(
    (afterId: string | null) => {
      const rows = document.querySelectorAll("[data-task-id]")
      // If inserting at top (afterId=null), all rows shift down
      let pastInsertPoint = afterId === null

      for (const row of rows) {
        const el = row as HTMLElement
        const taskId = el.getAttribute("data-task-id")

        if (pastInsertPoint) {
          // Shift this row down — preserves any existing transform by appending
          if (!el.style.transform.includes("fab-shift")) {
            el.style.transition = "transform 150ms ease-out"
            el.style.transform = `translateY(${ROW_SHIFT}px)`
          }
        } else {
          // Reset any previous shift
          if (el.style.transform.includes("translateY")) {
            el.style.transform = ""
            el.style.transition = ""
          }
        }

        if (taskId === afterId) {
          pastInsertPoint = true
        }
      }
    },
    [],
  )

  const clearRowShifts = useCallback(() => {
    const rows = document.querySelectorAll("[data-task-id]")
    for (const row of rows) {
      const el = row as HTMLElement
      el.style.transform = ""
      el.style.transition = ""
    }
  }, [])

  // -----------------------------------------------------------------------
  // Compute gap indicator screen position from afterId
  // Uses the SHIFTED row positions — call AFTER applyRowShifts.
  // -----------------------------------------------------------------------
  const updateGapPosition = useCallback(
    (afterId: string | null) => {
      const rows = document.querySelectorAll("[data-task-id]")
      if (rows.length === 0) {
        setGapPosition(null)
        return
      }

      if (afterId === null) {
        const firstRow = rows[0]!
        const rect = firstRow.getBoundingClientRect()
        // First row is shifted down — gap goes at its original position
        setGapPosition({
          top: rect.top - ROW_SHIFT,
          left: rect.left,
          width: rect.width,
        })
      } else {
        const targetRow = document.querySelector(
          `[data-task-id="${CSS.escape(afterId)}"]`,
        )
        if (!targetRow) {
          setGapPosition(null)
          return
        }
        const rect = targetRow.getBoundingClientRect()
        // afterId row is NOT shifted — gap starts at its bottom edge
        setGapPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
        })
      }
    },
    [],
  )

  // -----------------------------------------------------------------------
  // Auto-scroll — rAF loop during drag, speeds up near edges
  // -----------------------------------------------------------------------
  const startAutoScroll = useCallback(() => {
    scrollElRef.current = document.querySelector("main")

    const tick = () => {
      const state = dragStateRef.current
      const el = scrollElRef.current
      if (!state?.active || !el) {
        autoScrollRef.current = 0
        return
      }

      // Don't auto-scroll when pointer is over a magnetic zone
      if (state.dropTarget !== "list") {
        autoScrollRef.current = requestAnimationFrame(tick)
        return
      }

      // Don't auto-scroll until finger has moved enough from drag start
      if (Math.abs(state.y - dragStartYRef.current) < AUTO_SCROLL_ACTIVATE_DISTANCE) {
        autoScrollRef.current = requestAnimationFrame(tick)
        return
      }

      const rect = el.getBoundingClientRect()
      const pointerY = state.y
      let speed = 0

      if (pointerY < rect.top + AUTO_SCROLL_ZONE) {
        const dist = rect.top + AUTO_SCROLL_ZONE - pointerY
        speed = -(dist / AUTO_SCROLL_ZONE) * AUTO_SCROLL_MAX_SPEED
      } else if (pointerY > rect.bottom - AUTO_SCROLL_ZONE) {
        const dist = pointerY - (rect.bottom - AUTO_SCROLL_ZONE)
        speed = (dist / AUTO_SCROLL_ZONE) * AUTO_SCROLL_MAX_SPEED
      }

      if (speed !== 0) {
        el.scrollTop += speed
      }

      autoScrollRef.current = requestAnimationFrame(tick)
    }

    autoScrollRef.current = requestAnimationFrame(tick)
  }, [])

  const stopAutoScroll = useCallback(() => {
    if (autoScrollRef.current) {
      cancelAnimationFrame(autoScrollRef.current)
      autoScrollRef.current = 0
    }
  }, [])

  // -----------------------------------------------------------------------
  // Drag end — route by drop target
  // -----------------------------------------------------------------------
  const handleDragEnd = useCallback(async () => {
    const state = dragStateRef.current
    if (!state?.active) return

    setDragState(null)
    setGapPosition(null)
    stopAutoScroll()
    clearRowShifts()
    cancelSnap.reset()
    quickAddSnap.reset()

    // ── Cancel zone → abort ──────────────────────────────────────────
    if (state.dropTarget === "cancel") {
      hapticLight()
      return
    }

    // ── Quick-add zone → open Quick Add modal ──────────────────────
    if (state.dropTarget === "quick-add") {
      hapticMedium()
      // Switch to None resize mode before opening so Vaul doesn't flash
      if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("Keyboard")) {
        import("@capacitor/keyboard").then(({ Keyboard, KeyboardResize }) => {
          useKeyboardStore.getState().setResizeMode("none")
          void Keyboard.setResizeMode({ mode: KeyboardResize.None })
          void Keyboard.show()
        }).catch(() => {})
      }
      setQuickAddOpen(true)
      return
    }

    // ── List drop → create at position ───────────────────────────────
    hapticMedium()

    const defaults = getTaskDefaults(
      viewId,
      containerId ?? undefined,
      eveningHour,
    )

    if (state.mode === "section" && allowSection && containerId) {
      const result = await addSection("", `root/${containerId}`, {
        insertPosition: state.afterId
          ? { afterId: state.afterId }
          : "top",
      })
      // Focus the new section for inline editing
      if (result.ok) {
        const id = result.data.id
        const focusSection = (attempt = 0) => {
          const wrapper = document.querySelector(
            `[data-task-id="${CSS.escape(id)}"]`,
          )
          const target = wrapper?.querySelector<HTMLElement>("[data-section-row]")
          if (target) {
            target.dispatchEvent(new MouseEvent("dblclick", { bubbles: true }))
          } else if (attempt < 30) {
            requestAnimationFrame(() => focusSection(attempt + 1))
          }
        }
        requestAnimationFrame(() => focusSection())
      }
    } else {
      const result = await addTask({
        title: "",
        parentPath: defaults.parentPath,
        insertPosition: state.afterId
          ? { afterId: state.afterId }
          : "top",
        startAt: defaults.schedule?.startAt,
        startAtType: defaults.schedule?.type,
        startAtSubType: defaults.schedule?.subType,
      })
      if (result.ok) {
        openTask(result.data.id, true)
      }
    }
  }, [viewId, containerId, eveningHour, allowSection, openTask, stopAutoScroll, clearRowShifts, cancelSnap, quickAddSnap])

  // ── Touch handlers ──────────────────────────────────────────────────────

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (speedDialOpen) return
      const touch = e.touches[0]
      if (!touch) return
      touchStartRef.current = { x: touch.clientX, y: touch.clientY }

      pressTimerRef.current = setTimeout(() => {
        hapticMedium()
        cancelSnap.suppressUntilExit()
        quickAddSnap.reset()
        dragStartYRef.current = touch.clientY
        const initial: DragState = {
          active: true,
          x: touch.clientX,
          y: touch.clientY,
          mode: "task",
          afterId: null,
          dropTarget: "list",
          bouncing: false,
        }
        setDragState(initial)
        dragStateRef.current = initial
        startAutoScroll()
      }, LONG_PRESS_MS)
    },
    [speedDialOpen, startAutoScroll, cancelSnap, quickAddSnap],
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0]
      if (!touch) return

      // During long-press timer: check tolerance
      if (!dragStateRef.current?.active && touchStartRef.current) {
        const dx = touch.clientX - touchStartRef.current.x
        const dy = touch.clientY - touchStartRef.current.y
        if (Math.hypot(dx, dy) > MOVE_TOLERANCE) {
          clearTimeout(pressTimerRef.current)
          touchStartRef.current = null
        }
        return
      }

      if (!dragStateRef.current?.active) return
      e.preventDefault()

      const rawX = touch.clientX
      const rawY = touch.clientY

      // ── Compute zone positions from actual rendered elements ─────────
      // This accounts for safe-area-bottom, zoom, and all CSS transforms.
      const cancelEl = document.querySelector<HTMLElement>("[data-zone-cancel]")
      const quickAddEl = document.querySelector<HTMLElement>("[data-zone-quick-add]")

      const cancelZone: MagneticZone | null = cancelEl
        ? (() => {
            const r = cancelEl.getBoundingClientRect()
            return {
              cx: r.left + r.width / 2,
              cy: r.top + r.height / 2,
              enterRadius: SNAP_ENTER_RADIUS,
              exitRadius: SNAP_EXIT_RADIUS,
              pull: SNAP_PULL,
            }
          })()
        : null

      const quickAddZone: MagneticZone | null = quickAddEl
        ? (() => {
            const r = quickAddEl.getBoundingClientRect()
            return {
              cx: r.left + r.width / 2,
              cy: r.top + r.height / 2,
              enterRadius: SNAP_ENTER_RADIUS,
              exitRadius: SNAP_EXIT_RADIUS,
              pull: SNAP_PULL,
            }
          })()
        : null

      // ── Check snap zones ─────────────────────────────────────────────
      let finalX = rawX
      let finalY = rawY
      let dropTarget: DropTarget = "list"
      let justExitedAnyZone = false

      if (cancelZone) {
        const result = cancelSnap.snap(rawX, rawY, cancelZone)
        if (result.inside) {
          finalX = result.x
          finalY = result.y
          dropTarget = "cancel"
        }
        if (result.justExited) justExitedAnyZone = true
      }

      if (dropTarget === "list" && quickAddZone) {
        const result = quickAddSnap.snap(rawX, rawY, quickAddZone)
        if (result.inside) {
          finalX = result.x
          finalY = result.y
          dropTarget = "quick-add"
        }
        if (result.justExited) justExitedAnyZone = true
      }

      // ── Mode detection (only in list, not when snapped to a zone) ───
      const isSectionZone =
        dropTarget === "list" && allowSection && rawX < SECTION_ZONE_X
      const newMode: FabDragMode = isSectionZone ? "section" : "task"

      if (dragStateRef.current.mode !== newMode) {
        hapticLight()
      }

      // Haptic feedback when entering/leaving a zone
      if (dropTarget !== dragStateRef.current.dropTarget) {
        hapticLight()
      }

      // ── Find insert position (only when targeting the list) ──────────
      const afterId =
        dropTarget === "list"
          ? findInsertPosition(rawY).afterId
          : dragStateRef.current.afterId

      if (
        dropTarget === "list" &&
        afterId !== dragStateRef.current.afterId
      ) {
        hapticLight()
      }

      const updated: DragState = {
        ...dragStateRef.current,
        x: finalX,
        y: finalY,
        mode: newMode,
        afterId,
        dropTarget,
        bouncing: justExitedAnyZone || dragStateRef.current.bouncing,
      }
      setDragState(updated)
      dragStateRef.current = updated

      // Clear bounce flag after animation completes (~300ms)
      if (justExitedAnyZone) {
        setTimeout(() => {
          if (dragStateRef.current) {
            const cleared = { ...dragStateRef.current, bouncing: false }
            setDragState(cleared)
            dragStateRef.current = cleared
          }
        }, 300)
      }

      // Push rows apart only when targeting the list
      if (dropTarget === "list") {
        applyRowShifts(afterId)
        requestAnimationFrame(() => {
          updateGapPosition(afterId)
        })
      } else {
        clearRowShifts()
        setGapPosition(null)
      }
    },
    [
      allowSection,
      findInsertPosition,
      applyRowShifts,
      updateGapPosition,
      clearRowShifts,
      cancelSnap,
      quickAddSnap,
    ],
  )

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    clearTimeout(pressTimerRef.current)
    // This touch is handled here (close dial, drop or tap): the click the browser emulates after
    // it must not run the tap again.
    if (e.cancelable) e.preventDefault()
    touchHandledRef.current = true
    setTimeout(() => { touchHandledRef.current = false }, 600)
    // If speed dial is open, tapping the FAB just closes it
    if (speedDialOpen) {
      touchStartRef.current = null
      setSpeedDialOpen(false)
      // Suppress the synthetic click that follows touchEnd
      justClosedDialRef.current = true
      setTimeout(() => { justClosedDialRef.current = false }, 300)
      return
    }
    if (dragStateRef.current?.active) {
      void handleDragEnd()
    } else {
      touchStartRef.current = null
      void handleTap()
    }
  }, [handleDragEnd, handleTap, speedDialOpen])

  const handleTouchCancel = useCallback(() => {
    clearTimeout(pressTimerRef.current)
    touchStartRef.current = null
    setDragState(null)
    setGapPosition(null)
    stopAutoScroll()
    clearRowShifts()
  }, [stopAutoScroll, clearRowShifts])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeout(pressTimerRef.current)
      stopAutoScroll()
      clearRowShifts()
    }
  }, [stopAutoScroll, clearRowShifts])

  // -----------------------------------------------------------------------
  // Dummy task for drag overlay — memoized to avoid re-renders
  // -----------------------------------------------------------------------
  const dummyTask = useMemo(
    () => (dragState?.mode === "section" ? DUMMY_SECTION : DUMMY_TASK),
    [dragState?.mode],
  )

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (!visible) return null

  const isDragging = dragState?.active ?? false
  const isOverCancel = dragState?.dropTarget === "cancel"
  const isOverQuickAdd = dragState?.dropTarget === "quick-add"

  return (
    <>
      {/* ── Speed Dial Scrim ─────────────────────────────────────────── */}
      {speedDialOpen && (
        <div
          className="fixed inset-0 z-[39] bg-black/30 transition-opacity duration-200"
          onClick={() => setSpeedDialOpen(false)}
          onTouchEnd={(e) => {
            e.stopPropagation()
            setSpeedDialOpen(false)
          }}
        />
      )}

      {/* ── Speed Dial Menu Items ────────────────────────────────────── */}
      {speedDialOpen && (
        <div
          className="fixed z-[41] flex flex-col-reverse items-end gap-3"
          style={{
            right: FAB_RIGHT + (FAB_SIZE - ZONE_FAB_SIZE) / 2,
            bottom: `calc(var(--safe-area-bottom, 0px) + ${FAB_BOTTOM_OFFSET + FAB_SIZE + 12}px)`,
            zoom: `calc(1 / var(--content-zoom, 1))`,
          }}
        >
          {[
            {
              key: "quick-add",
              icon: <Inbox className="size-5" />,
              label: t("Quick Add"),
              enabled: true,
              action: () => {
                setSpeedDialOpen(false)
                hapticLight()
                // Switch to None resize mode + pre-show keyboard before opening
                if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("Keyboard")) {
                  import("@capacitor/keyboard").then(({ Keyboard, KeyboardResize }) => {
                    useKeyboardStore.getState().setResizeMode("none")
                    void Keyboard.setResizeMode({ mode: KeyboardResize.None })
                    void Keyboard.show()
                  }).catch(() => {})
                }
                // Short defer so the click event finishes before Vaul's Drawer mounts
                setTimeout(() => setQuickAddOpen(true), 50)
              },
            },
            {
              key: "section",
              icon: <Heading className="size-5" />,
              label: t("Section"),
              enabled: allowSection && !!containerId,
              action: () => {
                setSpeedDialOpen(false)
                if (!containerId) return
                void (async () => {
                  hapticLight()
                  const result = await addSection(
                    "",
                    `root/${containerId}`,
                    { insertPosition: "top" },
                  )
                  if (result.ok) {
                    const id = result.data.id
                    const focusSection = (attempt = 0) => {
                      const wrapper = document.querySelector(
                        `[data-task-id="${CSS.escape(id)}"]`,
                      )
                      const target =
                        wrapper?.querySelector<HTMLElement>(
                          "[data-section-row]",
                        )
                      if (target) {
                        target.dispatchEvent(
                          new MouseEvent("dblclick", { bubbles: true }),
                        )
                      } else if (attempt < 30) {
                        requestAnimationFrame(() =>
                          focusSection(attempt + 1),
                        )
                      }
                    }
                    requestAnimationFrame(() => focusSection())
                  }
                })()
              },
            },
            {
              key: "chat",
              icon: <MessageCircle className="size-5" />,
              label: t("Chat"),
              enabled: true,
              action: () => {
                setSpeedDialOpen(false)
                hapticLight()
                useViewContext.getState().setAiChatOpen(true)
              },
            },
            {
              key: "attachment",
              icon: <Paperclip className="size-5" />,
              label: t("Attachment"),
              enabled: false,
              action: () => {},
            },
          ].map((item, i) => (
            <button
              key={item.key}
              type="button"
              disabled={!item.enabled}
              onClick={item.action}
              className={cn(
                "flex items-center gap-2 rounded-full px-4 py-2.5 shadow-lg",
                "border transition-all duration-200",
                item.enabled
                  ? "bg-background text-foreground border-border active:scale-95"
                  : "bg-muted text-muted-foreground border-border/50 opacity-50",
              )}
              style={{
                animationDelay: `${i * SPEED_DIAL_STAGGER_MS}ms`,
                animation: `fab-dial-in 200ms ease-out ${i * SPEED_DIAL_STAGGER_MS}ms both`,
              }}
            >
              {item.icon}
              <span className="text-sm font-medium">{item.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* ── Cancel Zone FAB (bottom-right, ghost at FAB origin) ──────── */}
      {isDragging && (
        <>
          <div
            data-zone-cancel
            className={cn(
              "fixed z-40 rounded-full",
              "transition-all duration-200 ease-out",
              isOverCancel
                ? "bg-primary text-primary-foreground shadow-xl"
                : "bg-muted text-muted-foreground shadow-md",
            )}
            style={{
              width: isOverCancel ? "60vw" : FAB_SIZE,
              height: isOverCancel ? "60vw" : FAB_SIZE,
              right: isOverCancel ? `calc(${FAB_RIGHT}px + ${FAB_SIZE / 2}px - 30vw)` : FAB_RIGHT,
              bottom: isOverCancel ? `calc(var(--safe-area-bottom, 0px) + ${FAB_BOTTOM_OFFSET + FAB_SIZE / 2}px - 30vw)` : `calc(var(--safe-area-bottom, 0px) + ${FAB_BOTTOM_OFFSET}px)`,
              zoom: `calc(1 / var(--content-zoom, 1))`,
              animation: "fab-zone-in 200ms ease-out both",
            }}
          />
          {/* Icon pinned at original FAB center — never moves */}
          <div
            className="fixed z-[41] flex flex-col items-center justify-center pointer-events-none"
            style={{
              width: FAB_SIZE,
              height: FAB_SIZE,
              right: FAB_RIGHT,
              bottom: `calc(var(--safe-area-bottom, 0px) + ${FAB_BOTTOM_OFFSET}px)`,
              zoom: `calc(1 / var(--content-zoom, 1))`,
              color: isOverCancel ? "var(--primary-foreground)" : "var(--muted-foreground)",
              transition: "color 200ms ease-out",
            }}
          >
            {isOverCancel && (
              <span className="absolute text-sm font-semibold leading-tight text-center" style={{ bottom: '100%', marginBottom: 4 }}>
                {t("Cancel")}
              </span>
            )}
            <XIcon
              className="size-6"
              style={{
                transform: isOverCancel ? 'scale(1.5)' : undefined,
                transition: 'transform 200ms ease-out',
              }}
            />
          </div>
        </>
      )}

      {/* ── Quick Add Zone FAB (bottom-left, slides in during drag) ──── */}
      {isDragging && (
        <>
          <div
            data-zone-quick-add
            className={cn(
              "fixed z-40 rounded-full",
              "transition-all duration-200 ease-out",
              isOverQuickAdd
                ? "bg-primary text-primary-foreground shadow-xl"
                : "bg-muted text-muted-foreground shadow-md",
            )}
            style={{
              width: isOverQuickAdd ? "60vw" : ZONE_FAB_SIZE,
              height: isOverQuickAdd ? "60vw" : ZONE_FAB_SIZE,
              left: isOverQuickAdd ? `calc(${ZONE_LEFT}px + ${ZONE_FAB_SIZE / 2}px - 30vw)` : ZONE_LEFT,
              bottom: isOverQuickAdd ? `calc(var(--safe-area-bottom, 0px) + ${FAB_BOTTOM_OFFSET + (FAB_SIZE - ZONE_FAB_SIZE) / 2 + ZONE_FAB_SIZE / 2}px - 30vw)` : `calc(var(--safe-area-bottom, 0px) + ${FAB_BOTTOM_OFFSET + (FAB_SIZE - ZONE_FAB_SIZE) / 2}px)`,
              zoom: `calc(1 / var(--content-zoom, 1))`,
              animation: "fab-zone-in 200ms ease-out both",
            }}
          />
          {/* Icon pinned at original FAB center — never moves */}
          <div
            className="fixed z-[41] flex flex-col items-center justify-center pointer-events-none"
            style={{
              width: ZONE_FAB_SIZE,
              height: ZONE_FAB_SIZE,
              left: ZONE_LEFT,
              bottom: `calc(var(--safe-area-bottom, 0px) + ${FAB_BOTTOM_OFFSET + (FAB_SIZE - ZONE_FAB_SIZE) / 2}px)`,
              zoom: `calc(1 / var(--content-zoom, 1))`,
              color: isOverQuickAdd ? "var(--primary-foreground)" : "var(--muted-foreground)",
              transition: "color 200ms ease-out",
            }}
          >
            {isOverQuickAdd && (
              <span className="absolute text-sm font-semibold leading-tight text-center" style={{ bottom: '100%', marginBottom: 4 }}>
                {t("Add to")}<br />{t("Inbox")}
              </span>
            )}
            <Inbox
              className="size-5"
              style={{
                transform: isOverQuickAdd ? 'scale(1.5)' : undefined,
                transition: 'transform 200ms ease-out',
              }}
              strokeWidth={2}
            />
          </div>
        </>
      )}

      {/* ── Main FAB Button ──────────────────────────────────────────── */}
      <button
        data-fab-button
        type="button"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
        onClick={() => {
          if (justClosedDialRef.current || touchHandledRef.current) return
          if (speedDialOpen) {
            setSpeedDialOpen(false)
          } else {
            void handleTap()
          }
        }}
        className={cn(
          "fixed z-40 flex items-center justify-center rounded-full",
          "bg-primary text-primary-foreground shadow-lg",
          "transition-transform duration-200 ease-out",
          "active:scale-95",
          isDragging && "scale-110 shadow-2xl",
        )}
        style={{
          width: FAB_SIZE,
          height: FAB_SIZE,
          right: FAB_RIGHT,
          bottom: `calc(var(--safe-area-bottom, 0px) + ${FAB_BOTTOM_OFFSET}px)`,
          zoom: `calc(1 / var(--content-zoom, 1))`,
          touchAction: "none",
          // When dragging, hide the main FAB since cancel zone shows at that position
          opacity: isDragging ? 0 : 1,
          pointerEvents: isDragging ? "none" : "auto",
        }}
        aria-label={t("Add Task")}
      >
        <Plus
          className={cn(
            "size-7 transition-transform duration-200",
            speedDialOpen && "rotate-45",
          )}
          strokeWidth={2.5}
        />

        {/* ── Speed Dial "..." badge ───────────────────────────────── */}
        {/* Outer div = enlarged invisible hit area (top, top-right, right).
            Inner div = the visible badge at original BADGE_SIZE. */}
        {!isDragging && (
          <div
            className="absolute"
            style={{
              width: BADGE_SIZE + 12,
              height: BADGE_SIZE + 12,
              top: -(BADGE_SIZE / 2) + 4 - 12,
              right: -(BADGE_SIZE / 2) + 4 - 12,
            }}
            onTouchEnd={(e) => {
              e.stopPropagation()
              e.preventDefault()
              clearTimeout(pressTimerRef.current)
              touchStartRef.current = null
              hapticLight()
              setSpeedDialOpen((v) => !v)
            }}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              clearTimeout(pressTimerRef.current)
              touchStartRef.current = null
              setSpeedDialOpen((v) => !v)
            }}
          >
            <div
              className={cn(
                "absolute flex items-center justify-center rounded-full",
                "bg-primary-foreground text-primary",
                "transition-transform duration-150 active:scale-90",
              )}
              style={{
                width: BADGE_SIZE,
                height: BADGE_SIZE,
                bottom: 0,
                left: 0,
              }}
            >
              <span className="text-xs font-bold leading-none">···</span>
            </div>
          </div>
        )}
      </button>

      {/* Drag overlay — follows finger, renders the FAB icon itself.
          Visible in ALL drop targets so magnetic snap effect is visible.
          Grows with shadow when snapping to a zone.
          Bounces when exiting a zone (spring animation). */}
      {isDragging && dragState && (
        <div
          className="pointer-events-none fixed z-50 flex items-center justify-center rounded-full bg-primary text-primary-foreground"
          style={{
            width: dragState.dropTarget !== "list" ? FAB_SIZE * 1.2 : FAB_SIZE,
            height: dragState.dropTarget !== "list" ? FAB_SIZE * 1.2 : FAB_SIZE,
            left: dragState.x - (dragState.dropTarget !== "list" ? (FAB_SIZE * 1.2) / 2 : FAB_SIZE / 2),
            top: dragState.y - (dragState.dropTarget !== "list" ? (FAB_SIZE * 1.2) / 2 : FAB_SIZE / 2),
            boxShadow:
              dragState.dropTarget !== "list"
                ? "0 8px 30px rgba(0,0,0,0.3)"
                : "0 4px 12px rgba(0,0,0,0.15)",
            opacity: dragState.dropTarget !== "list" ? 0.7 : 0.9,
            transition: dragState.dropTarget !== "list"
              ? "width 200ms ease-out, height 200ms ease-out, box-shadow 200ms ease-out, opacity 200ms ease-out"
              : "width 100ms ease-out, height 100ms ease-out, box-shadow 100ms ease-out, opacity 100ms ease-out",
            animation: dragState.bouncing ? "fab-bounce 300ms ease-out" : undefined,
          }}
        >
          <Plus className="size-7" strokeWidth={2.5} />
        </div>
      )}

      {/* Gap indicator — shows where the item will land, sits in the gap */}
      {isDragging && gapPosition && dragState?.dropTarget === "list" && (
        <div
          key={`fab-gap-${dragState?.mode}`}
          className="pointer-events-none fixed z-[45] opacity-50"
          style={{
            left: gapPosition.left,
            top: gapPosition.top,
            width: gapPosition.width,
            transition: "top 150ms ease-out",
          }}
        >
          <TaskItem
            task={dummyTask}
            isDragOverlay
            isFirstInGroup
            onComplete={() => {}}
            onUncomplete={() => {}}
          />
        </div>
      )}

      {/* ── Quick Add Modal ────────────────────────────────────────── */}
      <QuickAddModal open={quickAddOpen} onOpenChange={setQuickAddOpen} />

      {/* ── CSS keyframes for zone animations ────────────────────────── */}
      <style>{`
        @keyframes fab-zone-in {
          from { opacity: 0; transform: scale(0.5); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes fab-dial-in {
          from { opacity: 0; transform: translateY(8px) scale(0.8); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes fab-bounce {
          0%   { transform: scale(1.15); }
          40%  { transform: scale(0.92); }
          70%  { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
      `}</style>
    </>
  )
})
